import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UserModel } from '../../models/resources/user.model';
import { normalizeListResponse, LaravelListEnvelope, NormalizedListResponse } from '../../helpers/api-envelope.helper';
import {
  AdminMailAccountPayload,
  AdminMailAccountsQuery,
  AssignmentResponse,
  ImportTriggerResponse,
  LastImportRunModel,
  MailAccountModel,
} from '../../models/admin/admin-mail-accounts.model';

/** Normalize last-import run from API (camelCase or snake_case) so the rest of the app sees MailAccountModel shape. */
function normalizeMailAccountLastImport(
  account: MailAccountModel | Record<string, unknown>
): MailAccountModel {
  const run =
    (account as MailAccountModel).lastImportRun ??
    (account as Record<string, unknown>)['last_import_run'];
  if (!run || typeof run !== 'object') {
    return { ...account } as MailAccountModel;
  }
  const r = run as Record<string, unknown>;
  const rawStatus = r['status'];
  const lastImportRun: LastImportRunModel = {
    startedAt: (r['startedAt'] ?? r['started_at']) as string | undefined,
    finishedAt: (r['finishedAt'] ?? r['finished_at']) as string | null | undefined,
    status: rawStatus != null ? String(rawStatus) : undefined,
    documentsImported: (r['documentsImported'] ?? r['documents_imported']) as number | null | undefined,
    errorMessage: (r['errorMessage'] ?? r['error_message']) as string | null | undefined,
  };
  return { ...account, lastImportRun } as MailAccountModel;
}

@Injectable({
  providedIn: 'root',
})
export class AdminMailAccountsService {
  constructor(private http: HttpClient) {}

  getMailAccounts(query: AdminMailAccountsQuery = {}): Observable<NormalizedListResponse<MailAccountModel>> {
    const params: Record<string, string> = {
      paginated: String(query.paginated ?? false),
      include: 'assignedUsers,lastImportRun',
    };

    if (query.search) {
      params['search'] = query.search;
    }
    if (query.ownerIds?.length) {
      params['owner_ids'] = query.ownerIds.join(',');
    }
    if (query.page !== undefined) {
      params['page'] = String(query.page);
    }
    if (query.perPage !== undefined) {
      params['per_page'] = String(query.perPage);
    }

    return this.http
      .get<MailAccountModel[] | LaravelListEnvelope<MailAccountModel>>(
        environment.apiUrl + '/mail-accounts',
        { params }
      )
      .pipe(
        map(normalizeListResponse),
        map((res) => ({
          ...res,
          data: res.data.map((account) => normalizeMailAccountLastImport(account)),
        }))
      );
  }

  getUsers(): Observable<NormalizedListResponse<UserModel>> {
    return this.http
      .get<UserModel[] | LaravelListEnvelope<UserModel>>(environment.apiUrl + '/users', {
        params: { paginated: 'false' },
      })
      .pipe(map(normalizeListResponse));
  }

  createMailAccount(payload: AdminMailAccountPayload): Observable<MailAccountModel> {
    return this.http.post<MailAccountModel>(environment.apiUrl + '/mail-accounts', payload);
  }

  updateMailAccount(id: number, payload: AdminMailAccountPayload): Observable<MailAccountModel> {
    return this.http.put<MailAccountModel>(environment.apiUrl + `/mail-accounts/${id}`, payload);
  }

  syncAssignments(mailAccountId: number, userIds: number[]): Observable<MailAccountModel> {
    return this.http
      .put<AssignmentResponse>(environment.apiUrl + `/mail-accounts/${mailAccountId}/assignments`, {
        user_ids: userIds,
      })
      .pipe(map((res) => res.data.mailAccount));
  }

  deleteMailAccount(mailAccountId: number): Observable<void> {
    return this.http.delete<void>(environment.apiUrl + `/mail-accounts/${mailAccountId}`);
  }

  /**
   * Trigger a manual import for the given mail account.
   * Backend creates an ImportRun and dispatches ImportMailJob; returns 202 on success, 409 if import already running.
   */
  triggerImport(mailAccountId: number): Observable<ImportTriggerResponse> {
    return this.http.post<ImportTriggerResponse>(
      environment.apiUrl + `/mail-accounts/${mailAccountId}/import`,
      {}
    );
  }
}
